import { useEffect, useRef, useState } from "react";
import { api, TOKEN_KEY, assetUrl } from "../lib/api";
import * as Icons from "lucide-react";

const ALL_ICONS = [
  "Sparkles", "Scissors", "Music2", "Shirt", "Crown", "Building2",
  "Camera", "Brush", "Compass", "Megaphone", "Newspaper", "Coins",
  "Star", "Award", "Diamond", "Flame", "Feather", "Eye",
  "Heart", "Gem", "Mic", "Palette", "Pen", "User",
];

const TABS = [
  { key: "hero", label: "Hero" },
  { key: "manifesto", label: "Manifesto" },
  { key: "identify", label: "Identify" },
  { key: "roles", label: "Roles" },
  { key: "form", label: "Form Fields" },
  { key: "counter", label: "Counter" },
  { key: "footer", label: "Footer" },
  { key: "submissions", label: "Submissions" },
  { key: "uploads", label: "Uploads" },
];

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [authChecking, setAuthChecking] = useState(true);

  useEffect(() => {
    let alive = true;
    const verify = async () => {
      if (!token) {
        if (alive) setAuthChecking(false);
        return;
      }
      try {
        await api.get("/admin/me");
        if (alive) setAuthChecking(false);
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        if (alive) {
          setToken(null);
          setAuthChecking(false);
        }
      }
    };
    verify();
    return () => {
      alive = false;
    };
  }, [token]);

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (authChecking) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <p className="text-cotton/40 tracking-[0.4em] uppercase text-xs">Checking…</p>
      </div>
    );
  }

  if (!token) {
    return <Login onSuccess={(t) => setToken(t)} />;
  }

  return <Dashboard onLogout={handleLogout} />;
};

// ============================================================
// Login
// ============================================================

const Login = ({ onSuccess }) => {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/login", { password: pw });
      localStorage.setItem(TOKEN_KEY, data.token);
      onSuccess(data.token);
    } catch (e) {
      setErr(e.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-6 font-condensed">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md border border-cotton/15 p-10 md:p-14 bg-black"
      >
        <p className="text-[10px] tracking-[0.5em] uppercase text-gold/80 mb-6">
          IFGL · Control
        </p>
        <h1
          className="text-cotton font-black uppercase leading-none tracking-[0.02em] mb-10"
          style={{ fontSize: "44px", fontWeight: 900 }}
        >
          The Vault<span className="text-red">.</span>
        </h1>

        <label className="block mb-8">
          <span className="block text-[10px] tracking-[0.4em] uppercase text-cotton/55 font-light mb-3">
            Password
          </span>
          <input
            data-testid="admin-password"
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            autoFocus
            autoComplete="current-password"
            className="ifgl-input"
          />
        </label>

        {err && (
          <p data-testid="login-error" className="text-red text-sm tracking-wide mb-6">
            {err}
          </p>
        )}

        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={busy || !pw}
          className="w-full bg-red text-cotton px-6 py-4 uppercase tracking-[0.35em] text-sm font-extrabold disabled:opacity-50 hover:bg-cotton hover:text-red transition-colors"
          style={{ fontWeight: 800 }}
        >
          {busy ? "Unlocking…" : "Enter"}
        </button>
      </form>
    </div>
  );
};

// ============================================================
// Dashboard
// ============================================================

const Dashboard = ({ onLogout }) => {
  const [tab, setTab] = useState("hero");
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingMsg, setSavingMsg] = useState("");

  const loadContent = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/content");
      setContent(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContent();
  }, []);

  const saveSection = async (section, value) => {
    setSavingMsg("Saving…");
    try {
      const { data } = await api.patch("/admin/content", { [section]: value });
      setContent(data);
      setSavingMsg("Saved ✓");
      setTimeout(() => setSavingMsg(""), 1800);
    } catch (e) {
      setSavingMsg("Save failed");
      setTimeout(() => setSavingMsg(""), 2200);
    }
  };

  const resetAll = async () => {
    if (!window.confirm("Reset ALL content to factory defaults? This cannot be undone.")) return;
    const { data } = await api.post("/admin/content/reset");
    setContent(data);
    setSavingMsg("Reset to defaults");
    setTimeout(() => setSavingMsg(""), 2200);
  };

  if (loading || !content) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <p className="text-cotton/40 tracking-[0.4em] uppercase text-xs">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-cotton font-condensed flex flex-col">
      {/* Top bar */}
      <header className="sticky top-0 z-20 border-b border-cotton/15 bg-black/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="font-black tracking-[0.18em] uppercase text-cotton" style={{ fontSize: 20, fontWeight: 900 }}>
              IFGL <span className="text-red">·</span> Control
            </span>
            <span className="hidden md:inline-block text-[10px] tracking-[0.35em] uppercase text-cotton/40">
              {savingMsg || "Live editor"}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              data-testid="admin-view-site"
              className="text-[10px] tracking-[0.35em] uppercase text-cotton/60 hover:text-gold transition-colors"
            >
              View Site →
            </a>
            <button
              data-testid="admin-reset"
              onClick={resetAll}
              className="text-[10px] tracking-[0.35em] uppercase text-cotton/45 hover:text-red transition-colors"
            >
              Reset All
            </button>
            <button
              data-testid="admin-logout"
              onClick={onLogout}
              className="text-[10px] tracking-[0.35em] uppercase border border-cotton/20 px-4 py-2 hover:border-red hover:text-red transition-colors"
            >
              Lock
            </button>
          </div>
        </div>

        {/* Tabs */}
        <nav className="max-w-7xl mx-auto px-6 flex gap-1 overflow-x-auto border-t border-cotton/10">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`whitespace-nowrap px-4 py-3 text-[11px] tracking-[0.35em] uppercase font-light border-b-2 transition-colors ${
                tab === t.key
                  ? "text-gold border-gold"
                  : "text-cotton/55 border-transparent hover:text-cotton hover:border-cotton/20"
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </header>

      {/* Body */}
      <main className="max-w-7xl mx-auto w-full px-6 py-10 flex-1">
        {tab === "hero" && <HeroEditor data={content.hero || {}} onSave={(v) => saveSection("hero", v)} />}
        {tab === "manifesto" && <ManifestoEditor data={content.manifesto || {}} onSave={(v) => saveSection("manifesto", v)} />}
        {tab === "identify" && <IdentifyEditor data={content.identify || {}} onSave={(v) => saveSection("identify", v)} />}
        {tab === "roles" && <RolesEditor data={content.roles || []} onSave={(v) => saveSection("roles", v)} />}
        {tab === "form" && <FormEditor data={content.form || {}} onSave={(v) => saveSection("form", v)} />}
        {tab === "counter" && <CounterEditor data={content.counter || {}} onSave={(v) => saveSection("counter", v)} />}
        {tab === "footer" && <FooterEditor data={content.footer || {}} onSave={(v) => saveSection("footer", v)} />}
        {tab === "submissions" && <SubmissionsViewer />}
        {tab === "uploads" && <UploadsManager />}
      </main>
    </div>
  );
};

// ============================================================
// Shared editor primitives
// ============================================================

const Label = ({ children }) => (
  <span className="block text-[10px] tracking-[0.4em] uppercase text-cotton/55 font-light mb-2">
    {children}
  </span>
);

const TextInput = ({ value, onChange, testId, type = "text", placeholder }) => (
  <input
    data-testid={testId}
    type={type}
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className="w-full bg-black border border-cotton/15 px-4 py-3 text-cotton focus:border-red outline-none transition-colors font-condensed font-light tracking-wide"
  />
);

const TextArea = ({ value, onChange, testId, rows = 3, placeholder }) => (
  <textarea
    data-testid={testId}
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    rows={rows}
    placeholder={placeholder}
    className="w-full bg-black border border-cotton/15 px-4 py-3 text-cotton focus:border-red outline-none transition-colors font-condensed font-light tracking-wide resize-y"
  />
);

const Section = ({ title, kicker, children, onSave, dirty, testId }) => (
  <section data-testid={testId} className="mb-12">
    <div className="flex items-end justify-between gap-4 mb-8">
      <div>
        {kicker && (
          <p className="text-[10px] tracking-[0.45em] uppercase text-gold/80 mb-3">{kicker}</p>
        )}
        <h2 className="text-cotton font-black uppercase tracking-[0.02em]" style={{ fontSize: 36, fontWeight: 900 }}>
          {title}
        </h2>
      </div>
      <button
        data-testid={`${testId}-save`}
        onClick={onSave}
        disabled={!dirty}
        className="bg-red text-cotton px-6 py-3 uppercase tracking-[0.3em] text-xs font-extrabold disabled:opacity-40 hover:bg-cotton hover:text-red transition-colors"
        style={{ fontWeight: 800 }}
      >
        Save
      </button>
    </div>
    <div className="space-y-6 border border-cotton/10 p-6 md:p-8">{children}</div>
  </section>
);

const ImageField = ({ label, value, onChange, testId }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
    } catch (err) {
      alert(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div>
      <Label>{label}</Label>
      <div className="flex items-center gap-4 flex-wrap">
        {value && (
          <div className="border border-cotton/15 p-2 bg-black">
            <img
              src={assetUrl(value)}
              alt=""
              className="h-20 w-auto max-w-[180px] object-contain"
            />
          </div>
        )}
        <div className="flex-1 min-w-[200px]">
          <TextInput value={value} onChange={onChange} testId={`${testId}-url`} placeholder="/assets/file.png or full URL" />
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={handleUpload}
          className="hidden"
          data-testid={`${testId}-file`}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid={`${testId}-upload`}
          className="border border-cotton/20 px-5 py-3 uppercase tracking-[0.3em] text-[11px] hover:border-gold hover:text-gold transition-colors disabled:opacity-50"
        >
          {uploading ? "Uploading…" : "Upload"}
        </button>
        {value && (
          <button
            type="button"
            onClick={() => onChange("")}
            data-testid={`${testId}-clear`}
            className="text-[11px] tracking-[0.3em] uppercase text-cotton/40 hover:text-red transition-colors"
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
};

// ============================================================
// Hero editor
// ============================================================

const HeroEditor = ({ data, onSave }) => {
  const [d, setD] = useState(data);
  useEffect(() => setD(data), [data]);
  const dirty = JSON.stringify(d) !== JSON.stringify(data);
  const set = (k, v) => setD((x) => ({ ...x, [k]: v }));

  return (
    <Section title="Hero" kicker="Section · The Threshold" testId="hero-editor" dirty={dirty} onSave={() => onSave(d)}>
      <ImageField label="Phoenix mark image" value={d.markUrl} onChange={(v) => set("markUrl", v)} testId="hero-mark" />
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <Label>Top-left tag</Label>
          <TextInput value={d.cornerTag} onChange={(v) => set("cornerTag", v)} testId="hero-corner" />
        </div>
        <div>
          <Label>Top-right tag</Label>
          <TextInput value={d.rightTag} onChange={(v) => set("rightTag", v)} testId="hero-right" />
        </div>
      </div>
      <div>
        <Label>Logotype text (huge)</Label>
        <TextInput value={d.logotype} onChange={(v) => set("logotype", v)} testId="hero-logotype" />
      </div>
      <div>
        <Label>Tagline</Label>
        <TextInput value={d.tagline} onChange={(v) => set("tagline", v)} testId="hero-tagline" />
      </div>
      <div>
        <Label>War line (red text)</Label>
        <TextInput value={d.warLine} onChange={(v) => set("warLine", v)} testId="hero-warline" />
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <Label>CTA label</Label>
          <TextInput value={d.ctaLabel} onChange={(v) => set("ctaLabel", v)} testId="hero-cta-label" />
        </div>
        <div>
          <Label>CTA link</Label>
          <TextInput value={d.ctaHref} onChange={(v) => set("ctaHref", v)} testId="hero-cta-href" />
        </div>
      </div>
      <div>
        <Label>Scroll indicator label</Label>
        <TextInput value={d.scrollLabel} onChange={(v) => set("scrollLabel", v)} testId="hero-scroll-label" />
      </div>
    </Section>
  );
};

// ============================================================
// Manifesto editor
// ============================================================

const ManifestoEditor = ({ data, onSave }) => {
  const [cols, setCols] = useState(data.columns || []);
  useEffect(() => setCols(data.columns || []), [data]);
  const dirty = JSON.stringify(cols) !== JSON.stringify(data.columns || []);

  const update = (i, key, val) => {
    setCols((arr) => arr.map((c, idx) => (idx === i ? { ...c, [key]: val } : c)));
  };
  const addCol = () => setCols((arr) => [...arr, { n: String(arr.length + 1).padStart(2, "0"), tag: "", title: "", body: "" }]);
  const remove = (i) => setCols((arr) => arr.filter((_, idx) => idx !== i));

  return (
    <Section title="Manifesto" kicker="Section · Three Columns" testId="manifesto-editor" dirty={dirty} onSave={() => onSave({ columns: cols })}>
      <div className="space-y-6">
        {cols.map((c, i) => (
          <div key={i} data-testid={`manifesto-row-${i}`} className="border border-cotton/10 p-5 grid md:grid-cols-12 gap-4">
            <div className="md:col-span-2">
              <Label>Number</Label>
              <TextInput value={c.n} onChange={(v) => update(i, "n", v)} testId={`manifesto-n-${i}`} />
            </div>
            <div className="md:col-span-3">
              <Label>Tag</Label>
              <TextInput value={c.tag} onChange={(v) => update(i, "tag", v)} testId={`manifesto-tag-${i}`} />
            </div>
            <div className="md:col-span-3">
              <Label>Title</Label>
              <TextInput value={c.title} onChange={(v) => update(i, "title", v)} testId={`manifesto-title-${i}`} />
            </div>
            <div className="md:col-span-4">
              <Label>Body</Label>
              <TextArea value={c.body} onChange={(v) => update(i, "body", v)} testId={`manifesto-body-${i}`} rows={2} />
            </div>
            <div className="md:col-span-12 flex justify-end">
              <button
                onClick={() => remove(i)}
                data-testid={`manifesto-remove-${i}`}
                className="text-[10px] tracking-[0.3em] uppercase text-cotton/40 hover:text-red"
              >
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>
      <button
        onClick={addCol}
        data-testid="manifesto-add"
        className="text-[11px] tracking-[0.3em] uppercase border border-cotton/20 px-4 py-2 hover:border-gold hover:text-gold transition-colors"
      >
        + Add column
      </button>
    </Section>
  );
};

// ============================================================
// Identify editor
// ============================================================

const IdentifyEditor = ({ data, onSave }) => {
  const [d, setD] = useState(data);
  useEffect(() => setD(data), [data]);
  const dirty = JSON.stringify(d) !== JSON.stringify(data);
  const set = (k, v) => setD((x) => ({ ...x, [k]: v }));

  return (
    <Section title="Identify Yourself" kicker="Section · The Audition" testId="identify-editor" dirty={dirty} onSave={() => onSave(d)}>
      <div>
        <Label>Prompt above the grid</Label>
        <TextInput value={d.prompt} onChange={(v) => set("prompt", v)} testId="id-prompt" />
      </div>
      <div>
        <Label>Audition tag</Label>
        <TextInput value={d.auditionTag} onChange={(v) => set("auditionTag", v)} testId="id-audition" />
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <Label>Heading line 1</Label>
          <TextInput value={d.headingLine1} onChange={(v) => set("headingLine1", v)} testId="id-line1" />
        </div>
        <div>
          <Label>Heading line 2</Label>
          <TextInput value={d.headingLine2} onChange={(v) => set("headingLine2", v)} testId="id-line2" />
        </div>
      </div>
      <div>
        <Label>Subtext</Label>
        <TextArea value={d.subtext} onChange={(v) => set("subtext", v)} testId="id-subtext" rows={3} />
      </div>
      <div>
        <Label>Submit button label</Label>
        <TextInput value={d.submitLabel} onChange={(v) => set("submitLabel", v)} testId="id-submit-label" />
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <Label>Success line 1</Label>
          <TextInput value={d.successLine1} onChange={(v) => set("successLine1", v)} testId="id-success1" />
        </div>
        <div>
          <Label>Success line 2</Label>
          <TextInput value={d.successLine2} onChange={(v) => set("successLine2", v)} testId="id-success2" />
        </div>
      </div>
    </Section>
  );
};

// ============================================================
// Roles editor
// ============================================================

const RolesEditor = ({ data, onSave }) => {
  const [rows, setRows] = useState(data);
  useEffect(() => setRows(data), [data]);
  const dirty = JSON.stringify(rows) !== JSON.stringify(data);

  const update = (i, key, val) => {
    setRows((arr) => arr.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
  };
  const remove = (i) => setRows((arr) => arr.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    setRows((arr) => {
      const a = [...arr];
      const j = i + dir;
      if (j < 0 || j >= a.length) return a;
      [a[i], a[j]] = [a[j], a[i]];
      return a;
    });
  };
  const add = () =>
    setRows((arr) => [...arr, { name: "New Role", tag: "The —", icon: "Sparkles", enabled: true }]);

  return (
    <Section title="Roles" kicker="Section · The Twelve" testId="roles-editor" dirty={dirty} onSave={() => onSave(rows)}>
      <div className="space-y-3">
        {rows.map((r, i) => {
          const Icon = Icons[r.icon] || Icons.Sparkles;
          return (
            <div
              key={i}
              data-testid={`role-row-${i}`}
              className="border border-cotton/10 p-4 grid md:grid-cols-12 gap-3 items-center"
            >
              <div className="md:col-span-1 flex items-center justify-center">
                <Icon size={28} strokeWidth={1} className="text-gold" />
              </div>
              <div className="md:col-span-3">
                <Label>Name</Label>
                <TextInput value={r.name} onChange={(v) => update(i, "name", v)} testId={`role-name-${i}`} />
              </div>
              <div className="md:col-span-2">
                <Label>Tag</Label>
                <TextInput value={r.tag} onChange={(v) => update(i, "tag", v)} testId={`role-tag-${i}`} />
              </div>
              <div className="md:col-span-3">
                <Label>Icon</Label>
                <select
                  value={r.icon}
                  onChange={(e) => update(i, "icon", e.target.value)}
                  data-testid={`role-icon-${i}`}
                  className="w-full bg-black border border-cotton/15 px-3 py-3 text-cotton focus:border-red outline-none"
                >
                  {ALL_ICONS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-1 flex flex-col items-center">
                <Label>On</Label>
                <input
                  type="checkbox"
                  checked={r.enabled !== false}
                  onChange={(e) => update(i, "enabled", e.target.checked)}
                  data-testid={`role-enabled-${i}`}
                  className="w-5 h-5 accent-red"
                />
              </div>
              <div className="md:col-span-2 flex items-center justify-end gap-3 text-cotton/40 text-lg">
                <button onClick={() => move(i, -1)} data-testid={`role-up-${i}`} className="hover:text-gold">↑</button>
                <button onClick={() => move(i, 1)} data-testid={`role-down-${i}`} className="hover:text-gold">↓</button>
                <button onClick={() => remove(i)} data-testid={`role-remove-${i}`} className="hover:text-red text-sm tracking-[0.3em] uppercase">×</button>
              </div>
            </div>
          );
        })}
      </div>
      <button
        onClick={add}
        data-testid="role-add"
        className="text-[11px] tracking-[0.3em] uppercase border border-cotton/20 px-4 py-2 hover:border-gold hover:text-gold transition-colors"
      >
        + Add role
      </button>
    </Section>
  );
};

// ============================================================
// Form fields editor
// ============================================================

const FormEditor = ({ data, onSave }) => {
  const [fields, setFields] = useState(data.fields || []);
  useEffect(() => setFields(data.fields || []), [data]);
  const dirty = JSON.stringify(fields) !== JSON.stringify(data.fields || []);

  const update = (i, key, val) => setFields((arr) => arr.map((f, idx) => (idx === i ? { ...f, [key]: val } : f)));
  const remove = (i) => setFields((arr) => arr.filter((_, idx) => idx !== i));
  const add = () => setFields((arr) => [...arr, { key: `field${arr.length + 1}`, label: "New field", type: "text" }]);

  return (
    <Section title="Application Form Fields" kicker="Section · The Audition Form" testId="form-editor" dirty={dirty} onSave={() => onSave({ fields })}>
      <p className="text-cotton/45 text-sm italic mb-2">
        The role selector is always shown. These are the fields below it.
      </p>
      <div className="space-y-3">
        {fields.map((f, i) => (
          <div key={i} className="border border-cotton/10 p-4 grid md:grid-cols-12 gap-3 items-center" data-testid={`field-row-${i}`}>
            <div className="md:col-span-3">
              <Label>Key (internal)</Label>
              <TextInput value={f.key} onChange={(v) => update(i, "key", v)} testId={`field-key-${i}`} />
            </div>
            <div className="md:col-span-6">
              <Label>Label</Label>
              <TextInput value={f.label} onChange={(v) => update(i, "label", v)} testId={`field-label-${i}`} />
            </div>
            <div className="md:col-span-2">
              <Label>Type</Label>
              <select
                value={f.type || "text"}
                onChange={(e) => update(i, "type", e.target.value)}
                data-testid={`field-type-${i}`}
                className="w-full bg-black border border-cotton/15 px-3 py-3 text-cotton focus:border-red outline-none"
              >
                <option value="text">text</option>
                <option value="email">email</option>
                <option value="tel">tel</option>
                <option value="url">url</option>
                <option value="phone">phone · country code</option>
              </select>
            </div>
            <div className="md:col-span-1 flex justify-end">
              <button onClick={() => remove(i)} data-testid={`field-remove-${i}`} className="text-cotton/40 hover:text-red">×</button>
            </div>
          </div>
        ))}
      </div>
      <button
        onClick={add}
        data-testid="field-add"
        className="text-[11px] tracking-[0.3em] uppercase border border-cotton/20 px-4 py-2 hover:border-gold hover:text-gold transition-colors"
      >
        + Add field
      </button>
    </Section>
  );
};

// ============================================================
// Counter editor
// ============================================================

const CounterEditor = ({ data, onSave }) => {
  const [d, setD] = useState(data);
  useEffect(() => setD(data), [data]);
  const dirty = JSON.stringify(d) !== JSON.stringify(data);
  const set = (k, v) => setD((x) => ({ ...x, [k]: v }));
  const updateStat = (i, key, val) =>
    set("stats", (d.stats || []).map((s, idx) => (idx === i ? { ...s, [key]: val } : s)));
  const addStat = () =>
    set("stats", [...(d.stats || []), { num: "0", label: "Label", tag: "Tag" }]);
  const removeStat = (i) =>
    set("stats", (d.stats || []).filter((_, idx) => idx !== i));

  return (
    <Section title="Counter / Ledger" kicker="Section · The Stats" testId="counter-editor" dirty={dirty} onSave={() => onSave(d)}>
      <div>
        <Label>Section kicker / tag</Label>
        <TextInput value={d.ledgerTag} onChange={(v) => set("ledgerTag", v)} testId="counter-tag" />
      </div>
      <div className="space-y-3">
        {(d.stats || []).map((s, i) => (
          <div key={i} className="border border-cotton/10 p-4 grid md:grid-cols-12 gap-3" data-testid={`stat-row-${i}`}>
            <div className="md:col-span-2">
              <Label>Number</Label>
              <TextInput value={s.num} onChange={(v) => updateStat(i, "num", v)} testId={`stat-num-${i}`} />
            </div>
            <div className="md:col-span-3">
              <Label>Label</Label>
              <TextInput value={s.label} onChange={(v) => updateStat(i, "label", v)} testId={`stat-label-${i}`} />
            </div>
            <div className="md:col-span-3">
              <Label>Tag</Label>
              <TextInput value={s.tag} onChange={(v) => updateStat(i, "tag", v)} testId={`stat-tag-${i}`} />
            </div>
            <div className="md:col-span-3">
              <Label>Source</Label>
              <select
                value={s.source || "static"}
                onChange={(e) => updateStat(i, "source", e.target.value)}
                data-testid={`stat-source-${i}`}
                className="w-full bg-black border border-cotton/15 px-3 py-3 text-cotton focus:border-red outline-none"
              >
                <option value="static">Static</option>
                <option value="applicants_count">Live · Applicants</option>
              </select>
            </div>
            <div className="md:col-span-1 flex justify-end items-center">
              <button onClick={() => removeStat(i)} data-testid={`stat-remove-${i}`} className="text-cotton/40 hover:text-red text-xl">×</button>
            </div>
          </div>
        ))}
        <button onClick={addStat} data-testid="stat-add" className="text-[11px] tracking-[0.3em] uppercase border border-cotton/20 px-4 py-2 hover:border-gold hover:text-gold transition-colors">
          + Add stat
        </button>
      </div>
      <div>
        <Label>Footnote</Label>
        <TextArea value={d.footnote} onChange={(v) => set("footnote", v)} testId="counter-footnote" rows={2} />
      </div>
    </Section>
  );
};

// ============================================================
// Footer editor
// ============================================================

const FooterEditor = ({ data, onSave }) => {
  const [d, setD] = useState(data);
  useEffect(() => setD(data), [data]);
  const dirty = JSON.stringify(d) !== JSON.stringify(data);
  const set = (k, v) => setD((x) => ({ ...x, [k]: v }));

  return (
    <Section title="Footer" kicker="Section · The Closing" testId="footer-editor" dirty={dirty} onSave={() => onSave(d)}>
      <ImageField label="Footer mark image" value={d.markUrl} onChange={(v) => set("markUrl", v)} testId="footer-mark" />
      <div>
        <Label>Wordmark text</Label>
        <TextInput value={d.logotype} onChange={(v) => set("logotype", v)} testId="footer-logotype" />
      </div>
      <div>
        <Label>Copyright / center text</Label>
        <TextInput value={d.copy} onChange={(v) => set("copy", v)} testId="footer-copy" />
      </div>
      <div>
        <Label>Status label (next to red dot)</Label>
        <TextInput value={d.statusLabel} onChange={(v) => set("statusLabel", v)} testId="footer-status" />
      </div>
    </Section>
  );
};

// ============================================================
// Submissions viewer
// ============================================================

const SubmissionsViewer = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/applications");
      setItems(data.items);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const markRead = async (id, read) => {
    await api.patch(`/admin/applications/${id}`, { read });
    setItems((arr) => arr.map((x) => (x.id === id ? { ...x, read } : x)));
    if (selected?.id === id) setSelected({ ...selected, read });
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    await api.delete(`/admin/applications/${id}`);
    setItems((arr) => arr.filter((x) => x.id !== id));
    if (selected?.id === id) setSelected(null);
  };

  const exportCsv = () => {
    const headers = ["created_at", "role", "name", "email", "phone_dial", "phone_number", "location", "portfolio", "pitch", "read"];
    const rows = items.map((it) =>
      headers.map((h) => `"${String(it[h] ?? "").replace(/"/g, '""')}"`).join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ifgl-applications-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section data-testid="submissions-viewer" className="mb-12">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-[10px] tracking-[0.45em] uppercase text-gold/80 mb-3">Section · Inbox</p>
          <h2 className="text-cotton font-black uppercase tracking-[0.02em]" style={{ fontSize: 36, fontWeight: 900 }}>
            Submissions <span className="text-cotton/30 font-light">· {items.length}</span>
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="submissions-refresh"
            onClick={load}
            className="text-[11px] tracking-[0.3em] uppercase border border-cotton/20 px-4 py-2 hover:border-gold hover:text-gold transition-colors"
          >
            Refresh
          </button>
          <button
            data-testid="submissions-export"
            onClick={exportCsv}
            disabled={!items.length}
            className="text-[11px] tracking-[0.3em] uppercase border border-cotton/20 px-4 py-2 hover:border-gold hover:text-gold transition-colors disabled:opacity-40"
          >
            Export CSV
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-cotton/40">Loading…</p>
      ) : items.length === 0 ? (
        <div className="border border-cotton/10 p-10 text-center text-cotton/40 italic">
          No applications yet.
        </div>
      ) : (
        <div className="grid md:grid-cols-12 gap-6">
          <ul className="md:col-span-5 border border-cotton/10 divide-y divide-cotton/10 max-h-[70vh] overflow-y-auto">
            {items.map((it) => (
              <li key={it.id}>
                <button
                  onClick={() => setSelected(it)}
                  data-testid={`submission-row-${it.id}`}
                  className={`w-full text-left p-5 transition-colors hover:bg-cotton/[0.03] ${
                    selected?.id === it.id ? "bg-cotton/[0.05]" : ""
                  }`}
                >
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <span className="text-[10px] tracking-[0.3em] uppercase text-gold">{it.role}</span>
                    <span className="text-[10px] tracking-[0.2em] uppercase text-cotton/35">
                      {new Date(it.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-cotton font-semibold">
                    {it.name || <span className="text-cotton/40 italic">(unnamed)</span>}
                  </p>
                  <p className="text-cotton/50 text-sm truncate">{it.email}</p>
                  {it.phone_number && (
                    <p className="text-cotton/45 text-sm tabular-nums">
                      +{it.phone_dial} {it.phone_number}
                    </p>
                  )}
                  {!it.read && (
                    <span className="inline-block mt-2 text-[9px] tracking-[0.4em] uppercase text-red">● new</span>
                  )}
                </button>
              </li>
            ))}
          </ul>

          <div className="md:col-span-7">
            {selected ? (
              <div className="border border-cotton/10 p-6 md:p-8 sticky top-32" data-testid="submission-detail">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <p className="text-[10px] tracking-[0.45em] uppercase text-gold mb-2">{selected.role}</p>
                    <h3 className="text-cotton font-black uppercase tracking-[0.02em]" style={{ fontSize: 28, fontWeight: 900 }}>
                      {selected.name || "Unnamed"}
                    </h3>
                  </div>
                  <span className="text-[10px] tracking-[0.25em] uppercase text-cotton/40">
                    {new Date(selected.created_at).toLocaleString()}
                  </span>
                </div>

                <DetailRow label="Email" value={selected.email} />
                <DetailRow
                  label="Contact"
                  value={
                    selected.phone_number
                      ? `+${selected.phone_dial} ${selected.phone_number}${
                          selected.phone_country ? `  (${selected.phone_country})` : ""
                        }`
                      : ""
                  }
                />
                <DetailRow label="Location" value={selected.location} />
                <DetailRow label="Portfolio" value={selected.portfolio} link />
                <DetailRow label="Pitch" value={selected.pitch} block />

                <div className="flex items-center gap-3 mt-8">
                  <button
                    onClick={() => markRead(selected.id, !selected.read)}
                    data-testid="submission-toggle-read"
                    className="text-[11px] tracking-[0.3em] uppercase border border-cotton/20 px-4 py-2 hover:border-gold hover:text-gold transition-colors"
                  >
                    {selected.read ? "Mark unread" : "Mark read"}
                  </button>
                  <a
                    href={selected.email ? `mailto:${selected.email}` : undefined}
                    className="text-[11px] tracking-[0.3em] uppercase border border-cotton/20 px-4 py-2 hover:border-gold hover:text-gold transition-colors"
                  >
                    Reply →
                  </a>
                  <button
                    onClick={() => remove(selected.id)}
                    data-testid="submission-delete"
                    className="ml-auto text-[11px] tracking-[0.3em] uppercase text-cotton/40 hover:text-red"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ) : (
              <div className="border border-cotton/10 p-10 text-center text-cotton/40 italic">
                Select a submission.
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

const DetailRow = ({ label, value, link, block }) => {
  if (!value) return null;
  return (
    <div className="py-3 border-t border-cotton/10 first:border-t-0">
      <p className="text-[10px] tracking-[0.4em] uppercase text-cotton/40 mb-1">{label}</p>
      {block ? (
        <p className="text-cotton whitespace-pre-wrap">{value}</p>
      ) : link ? (
        <a href={value.startsWith("http") ? value : `https://${value}`} target="_blank" rel="noreferrer" className="text-gold hover:text-cotton break-all">
          {value}
        </a>
      ) : (
        <p className="text-cotton break-all">{value}</p>
      )}
    </div>
  );
};

// ============================================================
// Uploads manager
// ============================================================

const UploadsManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const inputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/uploads");
      setItems(data.items);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || "Upload failed");
    } finally {
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (name) => {
    if (!window.confirm("Delete this file?")) return;
    await api.delete(`/admin/uploads/${name}`);
    setItems((arr) => arr.filter((x) => x.url.endsWith(name) === false));
    load();
  };

  const copy = (url) => {
    navigator.clipboard?.writeText(url);
  };

  return (
    <section data-testid="uploads-manager" className="mb-12">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-[10px] tracking-[0.45em] uppercase text-gold/80 mb-3">Section · Media</p>
          <h2 className="text-cotton font-black uppercase tracking-[0.02em]" style={{ fontSize: 36, fontWeight: 900 }}>
            Uploads <span className="text-cotton/30 font-light">· {items.length}</span>
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            onChange={handleUpload}
            className="hidden"
            data-testid="uploads-file"
          />
          <button
            onClick={() => inputRef.current?.click()}
            data-testid="uploads-add"
            className="bg-red text-cotton px-5 py-3 uppercase tracking-[0.3em] text-xs font-extrabold hover:bg-cotton hover:text-red transition-colors"
            style={{ fontWeight: 800 }}
          >
            + Upload
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-cotton/40">Loading…</p>
      ) : items.length === 0 ? (
        <div className="border border-cotton/10 p-10 text-center text-cotton/40 italic">
          No files yet. Upload images to use anywhere on the site.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {items.map((it) => (
            <div key={it._id || it.url} data-testid="upload-card" className="border border-cotton/10 bg-black p-3">
              <div className="bg-black aspect-square flex items-center justify-center overflow-hidden">
                <img src={assetUrl(it.url)} alt="" className="max-w-full max-h-full object-contain" />
              </div>
              <div className="mt-3 space-y-2">
                <p className="text-[10px] tracking-[0.2em] uppercase text-cotton/40 truncate">
                  {it.original_name || it.url.split("/").pop()}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => copy(it.url)}
                    className="flex-1 text-[10px] tracking-[0.3em] uppercase border border-cotton/15 py-2 hover:border-gold hover:text-gold transition-colors"
                  >
                    Copy URL
                  </button>
                  <button
                    onClick={() => remove(it.url.split("/").pop())}
                    className="text-[10px] tracking-[0.3em] uppercase text-cotton/40 hover:text-red px-2"
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default Admin;
